<template>
  <div
    class="input-container"
    :class="newDesign && 'new-design'"
  >
    <slot name="before" />
    <slot name="label">
      <label
        v-if="label"
        class="block"
        :class="{
          'field-heading': newDesign,
          'q-mb-sm': !description,
          'q-mb-xs': description,
        }"
      >
        {{ label }}
        <slot name="appendLabel" />
      </label>

      <div
        v-if="description"
        class="field-subtext q-mb-sm"
      >
        {{ description }}
      </div>
    </slot>
    <div
      class="input-wrapper"
      :class="{ prepend: hasPrependSlot, append: hasAppendSlot }"
    >
      <slot name="prepend" />

      <div
        ref="control"
        class="full-width"
        tabindex="-1"
      >
        <slot name="control" />
      </div>

      <slot name="append" />
    </div>
    <slot name="after" />

    <div
      v-if="dirty && errors"
      class="input-container__errors"
    >
      <span class="text-help-text">
        {{ errors[0] }}
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "InputWrapper",
};
</script>

<script setup>
import { computed, getCurrentInstance, ref, toRef, useSlots, watch } from "vue";

import useValidation from "shared/composables/useValidation";

const props = defineProps({
  modelValue: {
    // eslint-disable-next-line vue/prefer-prop-type-boolean-first
    type: [String, Boolean, Number, Object, Array, Date],
    default: null,
  },
  validations: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: "",
  },
  description: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  optional: {
    type: Boolean,
  },
  newDesign: {
    type: Boolean,
  },
});

defineEmits(["update:modelValue"]);
const slots = useSlots();

const { input, dirty, errors, isValid } = useValidation({
  modelValue: toRef(() => props.modelValue),
  validations: toRef(() => props.validations),
  optional: toRef(() => props.optional),
});

const { proxy } = getCurrentInstance();
const control = ref(null);

const hasPrependSlot = computed(() => Boolean(slots.prepend));
const hasAppendSlot = computed(() => Boolean(slots.append));

const findControlComponent = () => control.value.querySelector("[tabindex]");

/** @public */
const focus = () => {
  const component = findControlComponent();
  if (component?.focus) component.focus();
};

/** @public */
const blur = () => {
  const component = findControlComponent();
  if (component?.blur) component.blur();
};

Object.assign(proxy, {
  focus,
  blur,
});

watch(
  () => props.modelValue,
  (modelValue) => {
    input.value = modelValue;
  },
  { deep: true }
);

defineExpose({
  isValid,
});
</script>

<style lang="scss" scoped>
.input-container:not(.new-design) {
  .input-wrapper {
    display: flex;
    align-items: center;
    padding: 0;

    &.prepend {
      padding-left: 16px;
    }

    &.append {
      padding-right: 16px;
    }
  }
}

.new-design {
  .input-wrapper {
    --c-input-height: inherit;
    --c-input-padding-inline: inherit;

    border: inherit;
  }
}

.text-help-text {
  color: var(--s-color-danger);
}
</style>
